import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { PageLoader, PageActions, PasslyStepper } from '@app/components';
import { genericErrorString, active, defaultOrgId } from '@app/helpers';
import GettingStartedComponent from '@app/pages/password/components/importPasswords/gettingStarted.component';
import UploadPasswordImportZipComponent from '@app/pages/password/components/importPasswords/uploadPasswordImportZip.component';
import SecretKeyComponent from '@app/pages/password/components/importPasswords/secretKey.component';
import { IListPageBarEnabled, Organization, IVaultsDisplay, IVaultFolder } from '@app/models';
import { useTypedTranslation } from '@app/hooks';
import { addDetailsBreadcrumb, handleSortingMenu } from '@app/store/actions';
import DoImportComponent from './components/importPasswords/doImport.component';

const ImportPasswordsPage = () => {
    const { translation } = useTypedTranslation();
    const dispatch = useDispatch();

    const [orgAllowedToViewThisPage, setOrgAllowedToViewThisPage] = useState(false);
    const [activeStep, setActiveStep] = useState(0);
    const [uploadedFile, setUploadedFile] = useState([]);
    const [secretKey, setSecretKey] = useState('');
    const [org, setOrg] = useState('');
    const [vault, setVault] = useState('');
    const [folder, setFolder] = useState('');
    const [loading, setLoading] = useState(true);
    const [allVaults, setAllVaults] = useState<IVaultsDisplay[]>([]);
    const [allFolders, setAllFolders] = useState<IVaultFolder[]>([]);
    const [vaultObject, setVaultObject] = useState<any>({});

    const [errorStep, setErrorStep] = useState();
    const steps = getSteps();

    const availableOrgs: Organization[] = useSelector((state: any) => state.basic.shellConfig.organizations);
    const organizationFilter: string = useSelector((state: any) => state.organizationSelect.selectedOrganization);
    const [errorMessage, setErrorMessage] = useState<string | undefined>(undefined);

    const setListPageBarEnabledOptions: IListPageBarEnabled = {
        changeViewEnabled: false,
        filtersViewEnabled: false,
        orderByEnabled: false,
        organizationsEnabled: true,
        actionMenuEnabled: false
    };

    useEffect(() => {
        dispatch(handleSortingMenu(setListPageBarEnabledOptions));

        setTimeout(function () {
            dispatch(addDetailsBreadcrumb(translation('IMPORT_PASSWORDS')));
        }, 300);

    }, []);

    useEffect(() => {
        if (activeStep === steps.length) {
            let errors: string = '';

            // this is the last step so make sure all form fields are filled in.
            if (uploadedFile.length === 0 || vault === '' || folder === '') {
                // error
                errors += translation('UPLOAD') + ' ' + translation('IS_REQUIRED') + '. ';
                setActiveStep(1);
            }
            if (secretKey === '') {
                // error
                if (errors === '') {
                    setActiveStep(2);
                }

                errors += translation('IMPORTER_ENCRYPTION_KEY') + ' ' + translation('IS_REQUIRED');
            }

            setErrorMessage(errors);
        }
    }, [activeStep]);

    useEffect(() => {
        if (organizationFilter !== undefined) {
            setup();
        }
    }, [organizationFilter]);

    const setup = () => {
        let orgAllowedToView = false;

        availableOrgs.forEach(function (o: Organization) {
            if (o.provisioningState === active && ((o.license.passwordServerEnabled && o.id === organizationFilter) || organizationFilter === defaultOrgId)) {
                orgAllowedToView = true;
            }
        });

        setOrgAllowedToViewThisPage(orgAllowedToView);
        setLoading(false);
    };

    const handleError = (errorResponse: any) => {
        setErrorMessage(genericErrorString(errorResponse));
    };

    function getSteps() {
        return [translation('GETTING_STARTED'), translation('UPLOAD'),
            translation('IMPORTER_ENCRYPTION_KEY')];
    }

    function getStepContent(step: number) {
        switch (step) {
            case 0:
                return <GettingStartedComponent />;
            case 1:
                return <UploadPasswordImportZipComponent currentFile={uploadedFile} currentFolder={folder} currentVault={vault} currentAvailableFolders={allFolders}
                    currentAvailableVaults={allVaults} returnAllAvailableVaults={getAllVaults} returnAllAvailableFolders={getAllFolders}
                    returnFile={getFileUploaded} returnOrg={getOrg} returnVault={getVault} returnFolder={getFolder} currentVaultObject={vaultObject} />;
            case 2:
                return <SecretKeyComponent returnKey={getSecretKey} currentKey={secretKey} />;
            default:
                return 'Unknown step';
        }
    }

    const getFileUploaded = (file: any) => {
        setUploadedFile(file);
        setErrorMessage('');
    };

    const getOrg = (org: string) => {
        setOrg(org);
        setErrorMessage('');
    };

    const getVault = (vault: string, vaultObject: IVaultsDisplay) => {
        setVault(vault);
        setVaultObject(vaultObject);
        setErrorMessage('');
    };

    const getFolder = (folder: string) => {
        setFolder(folder);
        setErrorMessage('');
    };

    const getSecretKey = (key: string) => {
        setSecretKey(key);
        setErrorMessage('');
    };

    const getAllVaults = (vaults: IVaultsDisplay[]) => {
        setAllVaults(vaults);
        setErrorMessage('');
    };

    const getAllFolders = (vaults: IVaultFolder[]) => {
        setAllFolders(vaults);
        setErrorMessage('');
    };

    const handleNext = () => {
        setActiveStep(prevActiveStep => prevActiveStep + 1);
    };

    const handleBack = () => {
        setActiveStep(prevActiveStep => prevActiveStep - 1);
    };

    return (
        <>
            {orgAllowedToViewThisPage && <PasslyStepper steps={steps} activeStep={activeStep} errorStep={errorStep} />}
            {loading ?
                <PageLoader />
                :
                <div>
                    {!orgAllowedToViewThisPage ?
                        <div className='migrateOnPrem'>
                            <div className='migrateOnPrem__no-view'>{translation('ORG_CANNOT_VIEW')}</div>
                        </div>
                        :
                        <>
                            {activeStep === steps.length ? (
                                <DoImportComponent file={uploadedFile} vault={vault} vaultFolder={folder} org={org} encryptionKey={secretKey}  />
                            ) : (
                                    <div>
                                        {getStepContent(activeStep)}
                                        <PageActions
                                            primaryBtn={activeStep === steps.length - 1 ? translation('IMPORT_PASSWORDS') : translation('RESET_NEXT')}
                                            secondaryBtn={translation('BACK')}
                                            onClickPrimary={handleNext}
                                            onClickSecondary={activeStep !== 0 ? handleBack : undefined}
                                            justify='center'
                                        />
                                    </div>
                                )
                            }
                        </>
                    }
                </div>
            }

            {
                errorMessage &&
                <div className='modal__error'>{errorMessage}</div>
            }
        </>
    );

};

export default ImportPasswordsPage;